import { useWorkflows, workflowSelector } from '@/store/workflows';
import { NodeShell } from '../NodeShell';
import type { NodeProps } from '@xyflow/react';
import { useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { parseStreamerUrl, streamerPayloadFromParsed } from '@/lib/url-parse';

interface Data { workflowId: string }

export function SourceNode({ data }: NodeProps & { data: Data }) {
  const wf = useWorkflows(workflowSelector(data.workflowId));
  const setStreamerUrl = useWorkflows((s) => s.setStreamerUrl);
  const setTracking = useWorkflows((s) => s.setTracking);
  const pushToast = useWorkflows((s) => s.pushToast);
  const [busy, setBusy] = useState(false);
  const parsed = useMemo(() => (wf ? parseStreamerUrl(wf.streamerUrl) : null), [wf?.streamerUrl]);

  if (!wf) return null;

  async function track() {
    if (!wf || !parsed) return;
    setBusy(true);
    try {
      await api.addStreamer(streamerPayloadFromParsed(parsed)).catch((e: Error) => {
        if (!/already exists|conflict|409/i.test(e.message)) throw e;
      });
      await api.startMonitor(parsed.name);
      setTracking(wf.id, true, parsed.name);
      pushToast(`tracking ${parsed.platform}:${parsed.handle}`, wf.id);
    } catch (e) {
      pushToast(`failed: ${(e as Error).message.slice(0, 80)}`, wf.id);
    } finally {
      setBusy(false);
    }
  }

  async function stop() {
    if (!wf || !wf.streamerHandle) {
      setTracking(wf!.id, false);
      return;
    }
    setBusy(true);
    try {
      await api.stopMonitor(wf.streamerHandle);
      setTracking(wf.id, false);
      pushToast('tracking stopped', wf.id);
    } catch (e) {
      pushToast(`stop failed: ${(e as Error).message.slice(0, 80)}`, wf.id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <NodeShell title="Source & Tracking" state={wf.states.source} width={260} showLeftHandle={false}>
      <input
        className="w-full bg-ink-950 border border-ink-800 rounded-md px-2 py-1.5 text-xs text-ink-200 placeholder:text-ink-600 focus:outline-none focus:border-accent-500"
        placeholder="twitch.tv/handle · kick.com/handle · youtube.com/@…"
        value={wf.streamerUrl}
        onChange={(e) => setStreamerUrl(wf.id, e.target.value)}
        disabled={wf.tracking || busy}
      />
      {wf.streamerUrl && (
        <div className="mt-1.5 text-[10px] text-ink-500 lowercase">
          {parsed
            ? <>parsed → <span className="text-ink-300">{parsed.platform}</span> · {parsed.handle}</>
            : <span className="text-danger-500">unrecognised url</span>}
        </div>
      )}
      <div className="mt-2 flex gap-2">
        {!wf.tracking ? (
          <button
            className="flex-1 text-xs px-2 py-1 rounded-md bg-accent-500/90 hover:bg-accent-500 text-ink-950 font-medium disabled:opacity-40"
            onClick={track}
            disabled={busy || !parsed}
          >
            {busy ? 'starting…' : 'track'}
          </button>
        ) : (
          <button
            className="flex-1 text-xs px-2 py-1 rounded-md bg-ink-800 hover:bg-ink-700 text-ink-300 disabled:opacity-40"
            onClick={stop}
            disabled={busy}
          >{busy ? 'stopping…' : 'stop'}</button>
        )}
      </div>
    </NodeShell>
  );
}
